import type { ApolloClient } from "@apollo/client";
import { formatISO } from "date-fns";
import { DocumentType, gql } from "__generated__/gql";
import { assertPresence } from "utils/assertions";
import type { DocumentVariablesType } from "utils/apollo/hooks";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";

const mutation = gql(/* GraphQL */ `
  mutation SignedPutUrl(
    $filePath: String
    $file: SignedPutUrlFileInput!
    $metadata: [SignedPutUrlInputMetadataPair!]
  ) {
    signedPutUrl(
      input: { filePath: $filePath, file: $file, metadata: $metadata }
    ) {
      url
      key
    }
  }
`);

type Mutation = typeof mutation;
type MutationData = DocumentType<Mutation>;
export type SignedPutUrlData = NonNullable<MutationData["signedPutUrl"]>;

export type SignedPutUrlMetadata = { [key: string]: string };

export default function makeGetSignedPutUrl(
  client: ApolloClient<object>,
  performFetch: (
    variables: DocumentVariablesType<Mutation>,
  ) => Promise<SignedPutUrlData> = async (variables) => {
    const result = await client.mutate({
      context: ADMIN_CONTEXT,
      mutation,
      variables,
    });
    assertPresence(
      result.data?.signedPutUrl,
      "Could not get signed URL from response!",
    );
    return result.data.signedPutUrl;
  },
) {
  async function getSignedPutUrl(
    file: File,
    filePath?: string,
    metadata?: SignedPutUrlMetadata,
  ) {
    const { name, type, size, lastModified } = file;

    const metadataPairs =
      metadata &&
      Object.keys(metadata).map((key) => ({ key, value: metadata[key] }));

    const payload = await performFetch({
      filePath,
      file: {
        name,
        type,
        size,
        modifiedAt:
          lastModified && lastModified > 0
            ? formatISO(lastModified)
            : undefined,
      },
      metadata: metadataPairs,
    });

    return payload;
  }

  return getSignedPutUrl;
}
